import React, { useCallback, useEffect, useReducer, useState, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMEMTranslation } from '../../utils/text-utils';
import { addAvailablePaymentPartitions, setDefaultFilters, setFilters } from '../../ducks/filters/duck-filters';
import { useAppSelector, useAppDispatch } from '../../ducks/hooks';
import {
  getDefaultParticipation,
  getManagerPartitionGroup,
  getParticipationPartitionGroup,
  getPartition,
  getPaymentPartition,
  getPaymentsPartitionGroup,
} from '../../ducks/filters/utils-filters';
import { useTrackEvent } from '../../services/logging/hooks';
import { Button, FlyInPanel, Icon, Text, Option } from '../../providers/harmony-scope/lazy-enablers';
import { usePageContext } from '../../utils/page-context';
import { ComponentFilterParticipationPartitionGroup } from './component-filter-participation-partition-group';
import { ComponentFilterParticipation } from './component-filter-participation';
import { ComponentFilterParticipationCadence } from './component-filter-participation-cadence';
import { ComponentFilterManagerPartitionGroup } from './component-filter-manager-partition-group';
import { ComponentFilterParticipationPartition } from './component-filter-participation-partition';
import { ComponentFilterPerformanceCadence } from './component-filter-performance-cadence';
import { ComponentFilterManagerPartition } from './component-filter-manager-partition';
import { ComponentFilterPaymentsPartition } from './component-filter-payments-partition';
import {
  managerToPaymentFilterSync,
  managerToParticipationFilterSync,
  participationToManagerFilterSync,
  participationToPaymentFilterSync,
  useShowEarningsFilters,
  useShowManagerFilters,
  useShowPerformanceFilters,
  useShowParticipationFilters,
  paymentToParticipationFilterSync,
  paymentToManagerFilterSync,
  paymentToPerformanceFilterSync,
  managerToPerformanceFilterSync,
  participationToPerformanceFilterSync,
  performanceToParticipationFilterSync,
  performanceToManagerFilterSync,
  performanceToPaymentFilterSync,
} from './hooks';
import { useGetPaymentPartitionsQuery } from '../../ducks/api-ducks/earnings-duck/api-duck-earnings';
import { LoadingPlaceholder } from '../../shared-components/hoc-loading-placeholder';
import { ComponentFilterManagerCadence } from './component-filter-manager-cadence';
import { ComponentFilterPerformancePartition } from './component-filter-performance-partition';
import { Flags, useFlagValue } from '../../services/feature-flags/hooks';
import { EPartitionCadence } from '../../ducks/filters/types-filters';

interface IProps {
  closePanel: (applied?: boolean) => void;
}

const componentName = 'Filters Panel';
export const FilterPanel = (props: IProps) => {
  // Harmony FlyInPanel close interception
  const handleRequestClose = (event: any) => {
    if (event?.detail?.source === 'keyboard') {
      event.preventDefault();
      return;
    }
  };
  // Hooks
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const dispatch = useAppDispatch();
  const trackEvent = useTrackEvent();
  const [updateVal, forceUpdate] = useReducer((x) => x + 1, 0);

  /* Note: isPanelOpen will always be true since control of the open state is actually in its parent
   **       The state of "opening" here is to control the transition from closed to open since otherwise it would flick open.
   */
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  // Open panel gracefully
  React.useEffect(() => {
    setPanelOpen(true);
  }, []);
  const { closePanel: parentClosePanel } = props;
  // delay to call parent's close panel to allow css transition to play. lookup info on javascript event loop for more info.
  const closePanel = useCallback(
    (applied?: boolean) => {
      setTimeout(() => parentClosePanel(applied), 0);
    },
    [parentClosePanel]
  );
  const handlePanelClosing = () => {
    trackEvent('Filters panel closed', pageName, componentName);
    closePanel();
  };

  // global state accessors
  const filters = useAppSelector((state) => state.filters);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const appConfig = useAppSelector((state) => state.appConfig);
  const impersonation = useAppSelector((state) => state.impersonation);
  const enableMonthlyCadences = useFlagValue(Flags.FILTER_MONTHLY_CADENCES) || (impersonation.isImpersonating && appConfig.isBusinessPersona);

  // component state
  const [participationId, setParticipationId] = useState<string>(`${filters?.participationId}`);
  const [participationCadence, setParticipatonCadence] = useState(filters?.selectedParticipationPartitionGroup?.partitionCadence);
  const [participationPartition, setParticipationPartition] = useState(filters?.selectedParticipationPartition?.title);
  const [participationPartitionGroupName, setParticipationPartitionGroupName] = useState(
    filters?.selectedParticipationPartitionGroup?.partitionGroup
  );
  const [performanceCadence, setPerformanceCadence] = useState(filters?.selectedPerformancePartitionGroup?.partitionCadence);
  const [performancePartition, setPerformancePartition] = useState(filters?.selectedPerformancePartition?.title);
  const [managerPartitionGroupName, setManagerPartitionGroupName] = useState(filters?.selectedManagerPartitionGroup?.partitionGroup);
  const [managerCadence, setManagerCadence] = useState(filters?.selectedManagerPartitionGroup?.partitionCadence);
  const [managerPartition, setManagerPartition] = useState(filters?.selectedManagerPartition?.title);
  const [paymentPartition, setPaymentPartition] = useState(filters?.selectedPaymentPartition?.id);
  const [dynamicFiscalYearId, setDynamicFiscalYearId] = useState<string>();

  // API clients
  const { data: dataPaymentPartitions, isFetching: isFetchingPaymentPartitions } = useGetPaymentPartitionsQuery(
    dynamicFiscalYearId && filters.personnelNumber
      ? {
          fiscalYearId: `${dynamicFiscalYearId}`,
          personnelNumber: filters.personnelNumber,
        }
      : skipToken
  );

  // Components Guards
  const showParticipationFilters = useShowParticipationFilters();
  const showPerformanceFilters = useShowPerformanceFilters();
  const showManagerFilters = useShowManagerFilters();
  const showEarningsFilters = useShowEarningsFilters();

  // Effects
  // Effect to update the dynamic fiscal year id depending on the filter gurads
  // This is used to trigger the fetch and memoization of the available payment partitions
  // and saving them to state
  useEffect(() => {
    if (showParticipationFilters || showEarningsFilters || showPerformanceFilters) {
      setDynamicFiscalYearId(participationPartitionGroupName);
    } else if (showManagerFilters) {
      setDynamicFiscalYearId(managerPartitionGroupName);
    }
  }, [
    managerPartitionGroupName,
    participationPartitionGroupName,
    showPerformanceFilters,
    showEarningsFilters,
    showManagerFilters,
    showParticipationFilters,
  ]);

  // Effect to upate the available payment partitions when the FY is updated
  useEffect(() => {
    if (dataPaymentPartitions) {
      let paymentPartitionGroupName = '';
      // We extract the fiscalYear id from any partition and use it as the partition group name
      if (dataPaymentPartitions.length > 0) {
        paymentPartitionGroupName = `${dataPaymentPartitions[0].fiscalYearId}`;
      }
      // We memoize the available partitions for this fiscal year
      dispatch(
        addAvailablePaymentPartitions({
          partitionGroup: `${paymentPartitionGroupName}`,
          partitions: dataPaymentPartitions,
        })
      );
    }
  }, [dataPaymentPartitions, dispatch]);

  // Handlers
  const applyFilters = useCallback(() => {
    // First we make a shallow copy of existing filters state.
    let newFilters = {
      ...filters,
    };

    // Depending on the available components (via guards) we update the corresponding filters,
    // Keeping in sync what needs to be kept in sync, but allowing the possibility of having
    // Desynchronized filters.
    if (showPerformanceFilters) {
      if (!participationDetails || !performanceCadence || !participationPartitionGroupName) return;
      newFilters.selectedPerformancePartitionGroup = getParticipationPartitionGroup(
        performanceCadence,
        parseInt(participationId, 10),
        participationDetails,
        participationPartitionGroupName
      );
      newFilters.selectedPerformancePartitionGroup = {
        ...newFilters.selectedPerformancePartitionGroup,
        partitionGroup: participationPartitionGroupName,
        partitions: newFilters.selectedPerformancePartitionGroup?.partitions || [],
      };
      if (performancePartition) {
        newFilters.selectedPerformancePartition = getPartition(performancePartition, newFilters.selectedPerformancePartitionGroup);
      }
      newFilters.participationId = parseInt(participationId, 10);
      // Keep manager, participation, and payment filters in sync with performance filters
      newFilters = performanceToParticipationFilterSync(newFilters, participationDetails);
      newFilters = performanceToManagerFilterSync(newFilters, participationDetails);
      newFilters = performanceToPaymentFilterSync(newFilters);
    } else if (showParticipationFilters) {
      newFilters.participationId = parseInt(participationId, 10);
      if (!participationDetails || !participationCadence || !participationPartitionGroupName) return;
      newFilters.selectedParticipationPartitionGroup = getParticipationPartitionGroup(
        participationCadence,
        parseInt(participationId, 10),
        participationDetails,
        participationPartitionGroupName
      );
      if (newFilters.selectedParticipationPartitionGroup) {
        // We 'decorate' the selected PartitionGroup with the corresponding name (FY) since its not included
        // in the participation data
        newFilters.selectedParticipationPartitionGroup = {
          ...newFilters.selectedParticipationPartitionGroup,
          partitionGroup: participationPartitionGroupName,
        };
        if (participationPartition) {
          newFilters.selectedParticipationPartition = getPartition(participationPartition, newFilters.selectedParticipationPartitionGroup);
        }
      }
      // Keep manager, performance, and payment filters in sync with participation filters
      newFilters = participationToManagerFilterSync(newFilters, participationDetails);
      newFilters = participationToPerformanceFilterSync(newFilters);
      newFilters = participationToPaymentFilterSync(newFilters);
    } else if (showManagerFilters) {
      // The only reason for this is to allow NPM with participant participations to move to a non-NPM participation
      // to show all other participation related tabs.
      newFilters.participationId = parseInt(participationId, 10);
      // We firs set up the manager filters
      if (!participationDetails || !managerPartitionGroupName || !managerCadence) return;
      newFilters.selectedManagerPartitionGroup = getManagerPartitionGroup(managerPartitionGroupName, managerCadence, participationDetails);
      if (newFilters.selectedManagerPartitionGroup && managerPartition) {
        newFilters.selectedManagerPartition = getPartition(managerPartition, newFilters.selectedManagerPartitionGroup);
      }

      // Then we sync the participation, performance, and payment filters with manager filters
      if (participationCadence) {
        newFilters = managerToParticipationFilterSync(newFilters, participationDetails, participationCadence);
      }
      if (performanceCadence) {
        newFilters = managerToPerformanceFilterSync(newFilters, participationDetails, performanceCadence);
      }
      newFilters = managerToPaymentFilterSync(newFilters);
    } else if (showEarningsFilters) {
      // First, we set the Earnings filters
      if (!participationPartitionGroupName) return;
      newFilters.selectedPaymentPartitionGroup = getPaymentsPartitionGroup(
        participationPartitionGroupName, // ParticipationGroupName since we piggy back the component and available FY
        filters.availablePaymentPartitions || []
      );
      if (newFilters.selectedPaymentPartitionGroup && paymentPartition) {
        newFilters.selectedPaymentPartition = getPaymentPartition(paymentPartition, newFilters.selectedPaymentPartitionGroup);
      } else {
        // If no payment partitions where found, then set it to an empty array with the selected fiscal year as the groupName
        newFilters.selectedPaymentPartitionGroup = {
          partitionGroup: participationPartitionGroupName,
          partitions: [],
        };
        newFilters.selectedPaymentPartition = undefined;
      }
      // We verify the current participationId is valid on the selectedPaymentPartition.
      let validParticipationId = parseInt(participationId, 10);

      // If there is a valid selctedPaymentPartition, sync with participation filters
      if (participationDetails && newFilters.selectedPaymentPartition) {
        const possibleParticipations = participationDetails.coreParticipations.filter(
          (p) => `${p.fiscalYearId}` === newFilters.selectedPaymentPartitionGroup?.partitionGroup && p.isPlanActive
        );
        const partition = newFilters.selectedPaymentPartition;
        // Select the participation closer to the earnings end date
        const validParticipation = possibleParticipations.reduce((participationToSelect, participation) => {
          const currentMinDif = Math.abs(
            new Date(participationToSelect.participationEndDateTime).getTime() - new Date(partition.earningsEndDate).getTime()
          );
          const currentDiff = Math.abs(new Date(partition.earningsEndDate).getTime() - new Date(participation.participationEndDateTime).getTime());
          return currentDiff < currentMinDif ? participation : participationToSelect;
        }, possibleParticipations[0]);
        validParticipationId = validParticipation ? validParticipation.participationId : validParticipationId;
      } else {
        // If there is no valid selectedPaymentPartition, set participation filters to default of dynamic FY
        if (!participationDetails) return;
        const possibleParticipations = participationDetails.coreParticipations.filter((p) => `${p.fiscalYearId}` === dynamicFiscalYearId);
        const validParticipation = getDefaultParticipation(possibleParticipations);
        validParticipationId = validParticipation ? validParticipation.participationId : validParticipationId;
      }

      // Then we sync the participation filters with earnings filters
      // The selectionOfThe PartitionGroup does not need FY as its embeded in the participationDetails, so the FY is extracted
      newFilters.participationId = validParticipationId;
      newFilters = paymentToParticipationFilterSync(newFilters, participationDetails);
      newFilters = paymentToPerformanceFilterSync(newFilters, participationDetails);
      newFilters = paymentToManagerFilterSync(newFilters, participationDetails);
    }
    dispatch(setFilters(newFilters));
    trackEvent('Filters applied', pageName, componentName, { newFilters });
    closePanel(true);
  }, [
    filters,
    showPerformanceFilters,
    showParticipationFilters,
    showManagerFilters,
    showEarningsFilters,
    dispatch,
    trackEvent,
    pageName,
    closePanel,
    performanceCadence,
    participationId,
    participationDetails,
    participationPartitionGroupName,
    performancePartition,
    participationCadence,
    participationPartition,
    managerPartitionGroupName,
    managerCadence,
    managerPartition,
    paymentPartition,
    dynamicFiscalYearId,
  ]);

  // useEffects to update the filters on reset
  useEffect(() => {
    setParticipationId(`${filters?.participationId}`);
    setPaymentPartition(filters?.selectedPaymentPartition?.id);
    setPerformanceCadence(filters?.selectedPerformancePartitionGroup?.partitionCadence);
    setPerformancePartition(filters?.selectedPerformancePartition?.title);
    setParticipationPartition(filters?.selectedParticipationPartition?.title);
    setParticipatonCadence(filters?.selectedParticipationPartitionGroup?.partitionCadence);
    setParticipationPartitionGroupName(filters?.selectedParticipationPartitionGroup?.partitionGroup);
    setManagerCadence(filters?.selectedManagerPartitionGroup?.partitionCadence);
    setManagerPartitionGroupName(filters?.selectedManagerPartitionGroup?.partitionGroup);
    setManagerPartition(filters?.selectedManagerPartition?.title);
  }, [
    filters?.participationId,
    filters?.selectedManagerPartition?.title,
    filters?.selectedManagerPartitionGroup?.partitionCadence,
    filters?.selectedManagerPartitionGroup?.partitionGroup,
    filters?.selectedParticipationPartition?.title,
    filters?.selectedParticipationPartitionGroup?.partitionCadence,
    filters?.selectedParticipationPartitionGroup?.partitionGroup,
    filters?.selectedPaymentPartition?.id,
    filters?.selectedPerformancePartitionGroup?.partitionCadence,
    filters?.selectedPerformancePartition?.title,
    updateVal,
  ]);

  const resetButtonRef = useRef<any>(null);
  const ariaLiveRegionRef = useRef<HTMLDivElement>(null);
  const clearFiltersData = () => {
    if (appConfig && participationDetails) {
      trackEvent('Filters cleared', pageName, componentName);
      dispatch(setDefaultFilters({ appConfig, participationDetails }));
      forceUpdate();
      // Show ARIA message for filters reseted successfully using ref
      if (ariaLiveRegionRef.current) {
        ariaLiveRegionRef.current.textContent = t('ariaFiltersResetSuccess');
        setTimeout(() => {
          if (ariaLiveRegionRef.current) {
            ariaLiveRegionRef.current.textContent = '';
          }
        }, 3000);
      }
      // Keep focus on the reset button
      if (resetButtonRef.current) {
        // Harmony Button does not expose native button, so we query it
        const nativeBtn = resetButtonRef.current.querySelector('button');
        if (nativeBtn) {
          nativeBtn.focus();
        }
      }
    }
  };

  return (
    <FlyInPanel
      heading={t('filtersPanelTitle')}
      open={isPanelOpen}
      onHeFlyInAfterHide={handlePanelClosing}
      onHeFlyInRequestClose={handleRequestClose}
      style={{ zIndex: 3 }}
      contained
    >
      <Text appearance="paragraph">{t('filtersPanelParagraph1')}</Text>
      <br />
      <Text appearance="paragraph">{t('filtersPanelParagraph2')}</Text>
      <br />
      {(showParticipationFilters || showPerformanceFilters || showEarningsFilters) && (
        <ComponentFilterParticipationPartitionGroup // Payments and Participation share Group picker
          selectedFiscalYear={participationPartitionGroupName || ''}
          onChange={setParticipationPartitionGroupName}
        />
      )}
      {showManagerFilters && (
        <ComponentFilterManagerPartitionGroup selectedPartitionGroupName={managerPartitionGroupName || ''} onChange={setManagerPartitionGroupName} />
      )}
      {!showEarningsFilters && (
        <ComponentFilterParticipation
          selectedFiscalYear={showManagerFilters ? managerPartitionGroupName || '' : participationPartitionGroupName || ''}
          selectedParticipation={participationId}
          onChange={setParticipationId}
        />
      )}
      {showParticipationFilters && (
        <ComponentFilterParticipationCadence
          selectedParticipation={participationId}
          selectedPartitionGroup={participationPartitionGroupName || ''}
          selectedCadence={participationCadence!}
          onChange={setParticipatonCadence}
        />
      )}
      {showPerformanceFilters && (
        <ComponentFilterPerformanceCadence
          selectedParticipation={participationId}
          selectedPartitionGroup={participationPartitionGroupName || ''}
          selectedCadence={performanceCadence!}
          onChange={setPerformanceCadence}
        />
      )}
      {showManagerFilters && (
        <ComponentFilterManagerCadence
          selectedManagerPartitionGroupName={managerPartitionGroupName || ''}
          selectedCadence={managerCadence!}
          onChange={setManagerCadence}
        />
      )}
      {showParticipationFilters && (
        <ComponentFilterParticipationPartition
          selectedParticipation={participationId}
          selectedCadence={participationCadence!}
          selectedPartition={participationPartition || ''}
          selectedPartitionGroup={participationPartitionGroupName || ''}
          onChange={setParticipationPartition}
        />
      )}
      {showPerformanceFilters &&
        // Only show if not hiding monthly cadence
        !(!enableMonthlyCadences && performanceCadence === EPartitionCadence.Monthly) && (
          <ComponentFilterPerformancePartition
            selectedParticipation={participationId}
            selectedCadence={performanceCadence!}
            selectedPartition={performancePartition || ''}
            selectedPartitionGroup={participationPartitionGroupName || ''}
            onChange={setPerformancePartition}
          />
        )}
      {showManagerFilters && (
        <ComponentFilterManagerPartition
          selectedManagerCadence={managerCadence!}
          selectedParticipation={participationId}
          selectedPartitionGroupName={managerPartitionGroupName || ''}
          selectedPartition={managerPartition || ''}
          onChange={setManagerPartition}
        />
      )}
      {showEarningsFilters && (
        <LoadingPlaceholder isLoading={isFetchingPaymentPartitions}>
          <ComponentFilterPaymentsPartition
            selectedPartition={paymentPartition || ''}
            selectedPartitionGroup={participationPartitionGroupName || ''} // Payments piggy back on participation FY selection
            onChange={setPaymentPartition}
          />
        </LoadingPlaceholder>
      )}
      <Button slot="footer" appearance="primary" onClick={applyFilters}>
        {t('filtersPanelApply')}
      </Button>
      <Button slot="footer" appearance="secondary" onClick={() => setPanelOpen(false)}>
        {t('filtersPanelCancel')}
      </Button>
      <Button slot="footer" appearance="command" onClick={clearFiltersData} ref={resetButtonRef}>
        <Icon slot="start" name="clearfilter" />
        {t('filtersPanelResetFilter')}
      </Button>
      {/* ARIA live region for filter reset success, now using ref */}
      <div
        id="FiltersAppliedAriaLiveRegion"
        ref={ariaLiveRegionRef}
        aria-live="polite"
        style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
      />
      {/* This is a hack to pre-fetch the lazy loaded Option component so that it doesnt show blank comboboxes */}
      <Option hidden aria-hidden />
    </FlyInPanel>
  );
};
